import React, { useCallback, useState } from 'react';
import { InboxOutlined, PaperClipOutlined } from '@ant-design/icons';
import api from '@services/api';
import { ServiceRequestData } from '@services/types/Relationship/MyCalls/types';
import { Button, message, Modal, Row, Tooltip, Upload } from 'antd';
import { useTranslation } from 'react-i18next';

import * as S from './styles';

interface AttachmentSendingProps {
  request: ServiceRequestData | undefined;
  onMutateAttachment?: () => void;
}

const AttachmentSending = ({
  request,
  onMutateAttachment,
}: AttachmentSendingProps) => {
  const { t } = useTranslation('service-requests');
  const [openModal, setOpenModal] = useState(false);
  const [fileUploaded, setFileUploaded] = useState<File>();
  const [sendLoading, setSendLoading] = useState(false);

  const onClose = useCallback(() => {
    setOpenModal(false);
    setFileUploaded(undefined);
  }, []);

  const sendAttachment = useCallback(async () => {
    setSendLoading(true);
    try {
      if (fileUploaded) {
        const formData = new FormData();
        formData.append('file', fileUploaded);

        await api.post(`/service-proxy/service/request/fileUpload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          params: {
            objectId: request?.id,
            assetId: request?.asset?.assetNumber,
          },
        });

        onMutateAttachment?.();
        onClose();
      }
    } catch {
      message.error(t('msgs.errorAttachingFile'));
    }
    setSendLoading(false);
  }, [
    fileUploaded,
    onClose,
    onMutateAttachment,
    request?.asset?.assetNumber,
    request?.id,
    t,
  ]);

  return (
    <>
      <Modal
        open={openModal}
        width={420}
        onCancel={onClose}
        footer={false}
        destroyOnClose
      >
        <S.AttachmentsSendContainer>
          <Upload.Dragger
            maxCount={1}
            onRemove={() => setFileUploaded(undefined)}
            beforeUpload={file => {
              setFileUploaded(file as File);
              return false;
            }}
          >
            <S.UploaderContent>
              <InboxOutlined />
              <p>{t('msgs.uploadDescription')}</p>
            </S.UploaderContent>
          </Upload.Dragger>
          <Row>
            <Button
              type="primary"
              onClick={sendAttachment}
              disabled={!fileUploaded}
              loading={sendLoading}
            >
              {t('buttons.send')}
            </Button>
          </Row>
        </S.AttachmentsSendContainer>
      </Modal>
      <Tooltip title={t('msgs.attachFile')}>
        <Button
          type="link"
          size="large"
          icon={<PaperClipOutlined />}
          onClick={() => setOpenModal(true)}
        />
      </Tooltip>
    </>
  );
};

export default AttachmentSending;
